import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import MainURL from '../../MainURL';
import { loginPathAtom, loginStateAtom, userDataAtom } from '../../AtomStore';
import {
  isUserTypeUnset,
  mapLoginUserData,
  SelectableUserType,
  clearSignupUserType,
  setSignupUserType,
  USER_TYPE_LABELS,
  USER_TYPE_ROLE_HINTS,
} from './userTypeUtils';
import './Login.scss';
import SignupStepper from './SignupStepper';

const CARD_OPTIONS: Array<{
  type: SelectableUserType;
  subtitleEn: string;
  features: string[];
  icon: 'church' | 'minister' | 'member';
}> = [
  {
    type: 'CHURCH',
    subtitleEn: 'Church Recruiter',
    features: ['채용 공고 등록 및 관리', '사역자 이력서 열람', '맞춤 인재 추천'],
    icon: 'church',
  },
  {
    type: 'MINISTER',
    subtitleEn: 'Minister',
    features: ['이력서 등록 및 관리', '채용 공고 검색 및 지원', '맞춤 공고 알림'],
    icon: 'minister',
  },
  {
    type: 'MEMBER',
    subtitleEn: 'Church Member',
    features: ['수련회·예배사역 정보 이용', '게시판 및 커뮤니티 참여', '교회 성도 맞춤 콘텐츠'],
    icon: 'member',
  },
];

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChurchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v4" />
      <path d="M10 4h4" />
      <path d="M12 6L4 11v11h16V11l-8-5z" />
      <path d="M10 22v-6h4v6" />
    </svg>
  );
}

function MemberIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3 21v-1a5 5 0 0 1 5-5h2" />
      <path d="M17 14a4 4 0 0 1 4 4v3" />
    </svg>
  );
}

function MinisterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="6" r="3" />
      <path d="M5 21v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v2" />
      <path d="M12 10v4" />
      <path d="M10 12h4" />
    </svg>
  );
}

function CardIcon({ icon }: { icon: 'church' | 'minister' | 'member' }) {
  if (icon === 'church') return <ChurchIcon />;
  if (icon === 'minister') return <MinisterIcon />;
  return <MemberIcon />;
}

export default function UserTypeOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const signupFlow = Boolean(location.state?.signupFlow);
  const signupSort = location.state?.sort === 'sns' ? 'sns' : 'email';
  const signupData = location.state?.data;
  const [isLogin, setIsLogin] = useAtom(loginStateAtom);
  const [userData, setUserData] = useAtom(userDataAtom);
  const loginPath = useAtomValue(loginPathAtom);
  const [loadingType, setLoadingType] = useState<SelectableUserType | null>(null);

  useEffect(() => {
    if (signupFlow) return;

    const verifyToken = localStorage.getItem('refreshToken');

    if (!isLogin || !verifyToken) {
      navigate('/login', { replace: true });
      return;
    }

    if (!isUserTypeUnset(userData.userType)) {
      navigate(loginPath || '/', { replace: true });
    }
  }, [signupFlow, isLogin, userData.userType, loginPath, navigate]);

  const handleBack = () => {
    if (loadingType) return;
    if (signupFlow) {
      clearSignupUserType();
      navigate('/login');
      return;
    }
    handleLogout();
  };

  const handleLogout = () => {
    if (loadingType) return;
    localStorage.clear();
    setIsLogin(false);
    setUserData({
      userAccount: '',
      userNickName: '',
      userSort: '',
      userDetail: '',
      userType: '',
      grade: '',
      authInstitution: '',
      authChurch: '',
      authDepartment: '',
      authGroup: '',
    });
    navigate('/login', { replace: true });
  };

  const handleSelectType = async (targetType: SelectableUserType) => {
    if (loadingType) return;

    if (signupFlow) {
      setSignupUserType(targetType);
      if (signupSort === 'sns' && signupData) {
        navigate('/login/logisterDetail', {
          state: { signupFlow: true, sort: 'sns', data: signupData },
        });
        return;
      }
      navigate('/login/logister');
      return;
    }

    const verifyToken = localStorage.getItem('refreshToken');
    if (!verifyToken) {
      alert('로그인 정보가 만료되었습니다. 다시 로그인해주세요.');
      setIsLogin(false);
      navigate('/login', { replace: true });
      return;
    }

    setLoadingType(targetType);
    try {
      const res = await axios.post(`${MainURL}/login/changeusertype`, {
        verifyToken,
        targetType,
      });

      if (!res.data?.success) {
        alert(res.data?.message || '회원 유형 설정에 실패했습니다.');
        return;
      }

      if (res.data.refreshToken) {
        localStorage.setItem('refreshToken', res.data.refreshToken);
      }

      setUserData(mapLoginUserData({
        ...userData,
        ...(res.data.userData || {}),
        userType: res.data.userType,
      }));

      window.location.replace(loginPath || '/');
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : '회원 유형 설정에 실패했습니다. 다시 시도해주세요.';
      alert(message);
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <div className="lp">
      <main className="lp__page-wrap">
          {signupFlow && <SignupStepper activeStep={1} />}

          <div className="lp__auth-card">
            <div className="lp__auth-title-row">
              <div>
                <h2 className="lp__auth-title">회원가입</h2>
                <p className="lp__auth-subtitle">회원 유형을 선택해 주세요</p>
              </div>
              <button
                type="button"
                className="lp__type-back"
                onClick={handleBack}
                disabled={Boolean(loadingType)}
              >
                {signupFlow ? '← 이전' : '로그아웃'}
              </button>
            </div>

            <div className="lp__type-cards">
              {CARD_OPTIONS.map((card) => (
                <div
                  key={card.type}
                  className={`lp__type-card${loadingType ? ' lp__type-card--loading' : ''}`}
                  onClick={() => handleSelectType(card.type)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectType(card.type);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="lp__type-card-icon">
                    <CardIcon icon={card.icon} />
                  </div>
                  <h2 className="lp__type-card-title">{USER_TYPE_LABELS[card.type]}</h2>
                  <p className="lp__type-card-role">{USER_TYPE_ROLE_HINTS[card.type]}</p>
                  <p className="lp__type-card-en">{card.subtitleEn}</p>

                  <ul className="lp__type-feature-list">
                    {card.features.map((feature) => (
                      <li key={feature} className="lp__type-feature-item">
                        <span className="lp__type-check">
                          <CheckIcon />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className="lp__btn lp__btn--soft lp__btn--full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectType(card.type);
                    }}
                    disabled={Boolean(loadingType)}
                  >
                    {loadingType === card.type ? '처리 중...' : '이 유형으로 시작하기'}
                    <span className="lp__type-arrow">→</span>
                  </button>
                </div>
              ))}
            </div>

            <p className="lp__type-footer-note">
              회원유형은 <strong>마이페이지</strong>에서 변경하실 수 있습니다
            </p>
          </div>
      </main>
    </div>
  );
}
